import { columnChart, stackedBar } from '../charts.ts';
import { api } from '../console/api.ts';
import { carrierCell, carrierName } from '../console/carriers.ts';
import { empty, meter, pageHead, pill, reportButtons } from '../console/components.ts';
import {
  agentName,
  CURRENCY_NAME,
  type Cur,
  date,
  esc,
  fmt,
  icon,
  lineName,
  type Money,
  PRODUCT,
  period,
  q,
  STATUS,
} from '../console/format.ts';
import { type Actions, render } from '../console/router.ts';
import { me } from '../console/session.ts';
import { $ } from '../console/ui.ts';
import { optionTag, selectControl } from '../controls.ts';
import { QUOTE_LOST_REASON } from './cotizacion-detalle.ts';
import { type LeadStatsView, leadStatsHtml } from './prospectos.ts';
import { CLAIM_STATUS, type ClaimIndicators, lossRatios } from './siniestros.ts';

/** «Estadísticas»: production, collections, carrier money, commissions, quoting and operations, month by month. */

type ProdRow = { currency: Cur; policies: number; netPremium: Money; commission: Money };
/** How many policies and their annual net premium, in one currency (#318). */
type Count = { currency: Cur; policies: number; premium: Money };
/** The commission split between the agents and the agency, in one currency (#318). */
type SplitRow = {
  currency: Cur;
  lines: number;
  accrued: Money;
  reversed: Money;
  ceded: Money;
  net: Money;
  unshared: number;
};
type CommRow = {
  currency: Cur;
  accrued: Money;
  confirmed: Money;
  disputed: Money;
  settled: Money;
  clawbacks: Money;
  total: Money;
};
type Analytics = {
  window: { from: string; to: string; months: string[]; today: string };
  filters: { carrier: string | null; agentId: string | null; productLine: string | null };
  scope: Record<string, 'AGENCY' | 'OWN' | null>;
  carriers: string[];
  agents: string[];
  production: {
    totals: ProdRow[];
    byMonth: (ProdRow & { period: string })[];
    byAgent: (ProdRow & { agentId: string })[];
    byCarrier: (ProdRow & { carrier: string })[];
    byProductLine: (ProdRow & { productLine: string })[];
  } | null;
  collections: {
    byMonth: { period: string; currency: Cur; billed: Money; collected: Money }[];
    rate: { currency: Cur; due: Money; collected: Money; rate: number | null }[];
    aging: {
      currency: Cur;
      total: Money;
      buckets: { bucket: string; amount: Money; installments: number }[];
    }[];
    /** Unpaid receipts the carrier collects that Nubi did not read lately (not in the aging). */
    unconfirmed: {
      carrier: string;
      pending: Money;
      installments: number;
      lastSyncedOn: string | null;
    }[];
    topOverdue: {
      scheduleId: string;
      carrier: string;
      policyNumber: string;
      productLine: string;
      agentId: string;
      overdue: Money;
      installments: number;
      oldestDueDate: string;
      daysPastDue: number;
    }[];
  } | null;
  carrierMoney: {
    pendingRemittance: {
      carrier: string;
      pending: Money;
      installments: number;
      oldestPaidOn: string;
      daysWaiting: number;
    }[];
    statements: {
      carrier: string;
      period: string;
      currency: Cur;
      matchedLines: number;
      matched: Money;
      disputedLines: number;
      disputed: Money;
      difference: Money;
      claim: Money;
    }[];
  } | null;
  commissions: {
    totals: CommRow[];
    byAgent: (CommRow & { agentId: string })[];
    byAgentMonth: (CommRow & { agentId: string; period: string })[];
    /** Devengada, cedida al agente y neta de la agencia (#318); null for an agent. */
    split?: {
      totals: SplitRow[];
      byMonth: (SplitRow & { period: string })[];
      byCarrier: (SplitRow & { carrier: string })[];
      byProductLine: (SplitRow & { productLine: string })[];
    } | null;
  } | null;
  quoting: {
    totals: { quotes: number; issued: number; lost?: number; rate: number | null };
    byProductLine: { productLine: string; quotes: number; issued: number; rate: number | null }[];
    byCarrier: { carrier: string; quoted: number; issued: number; rate: number | null }[];
    averagePremium: { productLine: string; currency: Cur; quotes: number; average: Money }[];
    /** Quotes closed as lost, per reason (#314). */
    lostByReason?: { reason: string; quotes: number }[];
  } | null;
  leads: LeadStatsView | null;
  operations: {
    open: { carrier: string; stage: string; orders: number; overdue: number }[];
    openTotals: { orders: number; overdue: number };
    closed: { carrier: string; orders: number; averageHours: number | null; late: number }[];
    closedTotals: { orders: number; averageHours: number | null; late: number };
  } | null;
  /** The renewal rate of the periods ending in the range (#308), per agent, carrier and line. */
  renewals?: {
    total: RenewalStats;
    byAgent: { key: string; indicators: RenewalStats }[];
    byCarrier: { key: string; indicators: RenewalStats }[];
    byProductLine: { key: string; indicators: RenewalStats }[];
  } | null;
  /** Cancellations with effect in the range: how many, the premium lost and why (#318). */
  cancellations?: {
    totals: Count[];
    byReason: (Count & { reason: string })[];
    byMonth: (Count & { period: string })[];
    byCarrier: (Count & { carrier: string })[];
    byProductLine: (Count & { productLine: string })[];
    byAgent: (Count & { agentId: string })[];
    undated: number;
  } | null;
  /** New business against renewals, and the portfolio per customer on a day (#318). */
  portfolio?: {
    newBusiness: {
      totals: (Count & { kind: 'NEW' | 'RENEWAL' })[];
      byMonth: (Count & { kind: 'NEW' | 'RENEWAL'; period: string })[];
    };
    on: string;
    customers: number;
    singlePolicy: number;
    multiPolicy: number;
    policies: number;
    withoutCustomer: number;
    averageTenureDays: number | null;
    premium: { currency: Cur; customers: number; premium: Money; perCustomer: Money }[];
  } | null;
  /** The claims of the range and the loss ratio (ADR-0038), with the statistics' filters. */
  claims?: ClaimIndicators | null;
};
type RenewalStats = {
  due: number;
  renewed: number;
  lost: number;
  pending: number;
  rate: number | null;
  premium: { currency: Cur; retained: Money; lost: Money }[];
  lostByReason: Record<string, number>;
};

/** The filters of «Estadísticas» (null: the API's default — the last six months). */
const statsFilter: {
  from: string | null;
  to: string | null;
  carrier: string;
  agent: string;
  productLine: string;
} = { from: null, to: null, carrier: '', agent: '', productLine: '' };
/** Which currency the monthly charts draw (quetzales and dollars never share an axis). */
let statsCurrency: Cur = 'GTQ';
/** A new session's charts start in quetzales. */
export function resetStats() {
  statsCurrency = 'GTQ';
}

const AGING: Record<string, [label: string, color: string]> = {
  CURRENT: ['Por vencer (30 días)', '--aging-1'],
  D1_30: ['1–30 días', '--aging-2'],
  D31_60: ['31–60 días', '--aging-3'],
  D61_90: ['61–90 días', '--aging-4'],
  D90_PLUS: ['Más de 90 días', '--aging-5'],
};
const MONTH_SHORT = new Intl.DateTimeFormat('es-GT', { month: 'short' });
const monthShort = (p: string) => MONTH_SHORT.format(new Date(`${p}-15T12:00:00`)).replace('.', '');
const COMPACT = new Intl.NumberFormat('es-GT', { notation: 'compact', maximumFractionDigits: 1 });
const qAxis = (minor: number, currency: Cur) =>
  `${currency === 'GTQ' ? 'Q' : 'US$'}${COMPACT.format(minor / 100)}`;
const rateText = (r: number | null) => (r === null ? '—' : `${(r * 100).toFixed(1)} %`);

/** Months from `today` back 23 (the range the filter offers), newest first. */
function monthOptions(today: string) {
  const [y, m] = today.split('-').map(Number) as [number, number];
  return Array.from({ length: 24 }, (_, i) => {
    const t = y * 12 + (m - 1) - i;
    return `${Math.floor(t / 12)}-${String((t % 12) + 1).padStart(2, '0')}`;
  });
}

export function statsQuery() {
  const p = new URLSearchParams();
  if (statsFilter.from) p.set('from', statsFilter.from);
  if (statsFilter.to) p.set('to', statsFilter.to);
  if (statsFilter.carrier) p.set('carrier', statsFilter.carrier);
  if (statsFilter.agent) p.set('agent', statsFilter.agent);
  if (statsFilter.productLine) p.set('productLine', statsFilter.productLine);
  const s = p.toString();
  return s ? `?${s}` : '';
}

function statsFilters(a: Analytics) {
  const months = monthOptions(a.window.today);
  const opt = optionTag;
  const select = (id: string, label: string, options: string) =>
    `<div class="field">${selectControl({ label, id }, options)}</div>`;
  const monthSelect = (id: string, label: string, value: string) =>
    select(id, label, months.map((p) => opt(p, period(p), p === value)).join(''));
  const agency = a.agents.length > 1;
  return `<form class="card toolbar stats-filters" id="stats-filters" aria-label="Filtros de las estadísticas">
    ${monthSelect('st-from', 'Desde', a.window.from)}
    ${monthSelect('st-to', 'Hasta', a.window.to)}
    ${select('st-carrier', 'Aseguradora', `${opt('', 'Todas', !a.filters.carrier)}${a.carriers.map((c) => opt(c, carrierName(c), c === a.filters.carrier)).join('')}`)}
    ${agency ? select('st-agent', 'Agente', `${opt('', 'Toda la agencia', !a.filters.agentId)}${a.agents.map((x) => opt(x, agentName(x), x === a.filters.agentId)).join('')}`) : ''}
    ${select(
      'st-line',
      'Ramo',
      `${opt('', 'Todos', !a.filters.productLine)}${Object.keys(PRODUCT)
        .map((l) => opt(l, lineName(l), l === a.filters.productLine))
        .join('')}`,
    )}
    <p class="muted small stats-scope">${icon(agency ? 'users' : 'lock')}${agency ? 'Toda la agencia' : 'Solo tus cifras'} · al ${esc(date(a.window.today))}</p>
  </form>`;
}

/** A table behind a disclosure: every chart's values without hovering (accessibility). */
const tableFallback = (head: string[], rows: string[][]) =>
  `<details class="viz-table"><summary>Ver tabla</summary><div class="table-wrap" data-component="table"><table class="table">
    <thead><tr>${head.map((h, i) => `<th${i ? ' class="num"' : ''}>${esc(h)}</th>`).join('')}</tr></thead>
    <tbody>${rows.map((r) => `<tr>${r.map((c, i) => `<td${i ? ' class="num"' : ''}>${esc(c)}</td>`).join('')}</tr>`).join('')}</tbody></table></div></details>`;

const card = (title: string, body: string, aside = '') =>
  `<section class="card"><div class="card-head"><h2>${esc(title)}</h2><span class="spacer"></span>${aside}</div>${body}</section>`;
const table = (head: string[], rows: string[], caption: string) =>
  rows.length
    ? `<div class="table-wrap" data-component="table"><table class="table"><caption class="sr-only">${esc(caption)}</caption><thead><tr>${head
        .map(
          (h, i) =>
            `<th${i && !h.startsWith('<') ? ' class="num"' : ''}>${h.startsWith('<') ? h.slice(1) : esc(h)}</th>`,
        )
        .join('')}</tr></thead><tbody>${rows.join('')}</tbody></table></div>`
    : '<p class="muted card-pad">Sin datos en este rango.</p>';

function statsKpis(a: Analytics) {
  const tiles: string[] = [];
  const tile = (iconName: string, label: string, value: string, foot: string, warn = false) =>
    `<div class="card kpi ${warn ? 'warn' : ''}"><span class="kpi-label">${icon(iconName)}${esc(label)}</span><span class="kpi-value">${value}</span><span class="kpi-foot">${foot}</span></div>`;
  const byCur = <T extends { currency: Cur }>(xs: T[]) => ({
    GTQ: xs.find((x) => x.currency === 'GTQ'),
    USD: xs.find((x) => x.currency === 'USD'),
  });
  if (a.production) {
    const t = byCur(a.production.totals);
    const policies = a.production.totals.reduce((s, x) => s + x.policies, 0);
    const usd = (m: Money | undefined, what: string) => (m ? ` · y ${esc(fmt(m))} ${what}` : '');
    tiles.push(
      tile('shield', 'Pólizas emitidas', String(policies), `${a.window.months.length} meses`),
      tile(
        'receipt',
        'Prima neta',
        esc(fmt(t.GTQ?.netPremium ?? { amountMinor: 0, currency: 'GTQ' })),
        `emitida en el rango${usd(t.USD?.netPremium, 'en dólares')}`,
      ),
      tile(
        'settle',
        'Comisión de la agencia',
        esc(fmt(t.GTQ?.commission ?? { amountMinor: 0, currency: 'GTQ' })),
        `anual, según la tarifa${usd(t.USD?.commission, 'en dólares')}`,
      ),
    );
  }
  if (a.collections) {
    const r = byCur(a.collections.rate);
    const g = r.GTQ ?? r.USD;
    tiles.push(
      tile(
        'trend',
        'Tasa de cobro',
        g ? rateText(g.rate) : '—',
        g
          ? `${esc(fmt(g.collected))} de ${esc(fmt(g.due))} vencidos${r.USD && g !== r.USD ? ` · dólares ${rateText(r.USD.rate)}` : ''}`
          : 'Nada venció en el rango',
      ),
    );
    const late = a.collections.aging.map((x) => ({
      amountMinor: x.buckets
        .filter((b) => b.bucket !== 'CURRENT')
        .reduce((s, b) => s + b.amount.amountMinor, 0),
      currency: x.currency,
    }));
    const lateG = late.find((x) => x.currency === 'GTQ');
    const lateU = late.find((x) => x.currency === 'USD');
    const any = late.some((x) => x.amountMinor > 0);
    tiles.push(
      tile(
        'warn',
        'Saldo vencido',
        esc(fmt(lateG ?? lateU ?? { amountMinor: 0, currency: 'GTQ' })),
        any
          ? `${a.collections.topOverdue.length} pólizas con atraso${lateG && lateU?.amountMinor ? ` · y ${esc(fmt(lateU))}` : ''}`
          : 'Todo al día',
        any,
      ),
    );
  }
  if (a.operations) {
    const o = a.operations.openTotals;
    tiles.push(
      tile(
        'inbox',
        'Órdenes abiertas',
        String(o.orders),
        o.overdue ? `${o.overdue} fuera del tiempo acordado` : 'Todas a tiempo',
        o.overdue > 0,
      ),
    );
  }
  if (a.quoting) {
    const t = a.quoting.totals;
    tiles.push(
      tile(
        'quote',
        'Cotización a póliza',
        rateText(t.rate),
        `${t.issued} de ${t.quotes} cotizaciones`,
      ),
    );
  }
  if (a.cancellations) {
    const c = a.cancellations;
    const n = c.totals.reduce((x, t) => x + t.policies, 0);
    tiles.push(
      tile(
        'close',
        'Anulaciones',
        String(n),
        n
          ? `${esc(c.totals.map((t) => fmt(t.premium)).join(' · '))} de prima perdida`
          : 'Ninguna en el rango',
        n > 0,
      ),
    );
  }
  if (a.portfolio) {
    const p = a.portfolio;
    tiles.push(
      tile(
        'users',
        'Clientes con pólizas',
        String(p.customers),
        p.customers
          ? `${p.multiPolicy} con varias pólizas (${Math.round((p.multiPolicy / p.customers) * 100)} %)`
          : `Ninguno al ${esc(date(p.on))}`,
      ),
    );
  }
  const net = a.commissions?.split?.totals ?? [];
  if (net.length) {
    const g = net.find((x) => x.currency === 'GTQ') ?? (net[0] as SplitRow);
    const other = net.filter((x) => x !== g);
    tiles.push(
      tile(
        'settle',
        'Comisión neta de la agencia',
        esc(fmt(g.net)),
        `después de ${esc(fmt(g.ceded))} cedidos a los agentes${other.map((x) => ` · y ${esc(fmt(x.net))}`).join('')}`,
      ),
    );
  }
  return tiles.length
    ? `<section class="kpis" aria-label="Indicadores">${tiles.join('')}</section>`
    : '';
}

function monthly<T extends { period: string; currency: Cur }>(
  a: Analytics,
  rows: T[],
  pick: (r: T) => number,
) {
  return a.window.months.map((p) => {
    const r = rows.find((x) => x.period === p && x.currency === statsCurrency);
    return r ? pick(r) : 0;
  });
}

function statsCharts(a: Analytics) {
  const categories = a.window.months.map((p) => ({
    key: p,
    short: monthShort(p),
    long: period(p),
  }));
  const cur = statsCurrency;
  const value = (minor: number) => q(minor, cur);
  const axis = (minor: number) => qAxis(minor, cur);
  const currencies = new Set<Cur>([
    ...(a.production?.byMonth ?? []).map((r) => r.currency),
    ...(a.collections?.byMonth ?? []).map((r) => r.currency),
  ]);
  const switcher =
    currencies.has('USD') && currencies.size > 1
      ? `<div class="seg" role="group" aria-label="Moneda de las gráficas">${(
          ['GTQ', 'USD'] as Cur[]
        )
          .map(
            (c) =>
              `<button type="button" class="btn sm${c === cur ? ' primary' : ''}" data-stats-currency="${c}" aria-pressed="${c === cur}">${c === 'GTQ' ? 'Quetzales' : 'Dólares'}</button>`,
          )
          .join('')}</div>`
      : '';
  let html = '';
  if (a.production) {
    const net = monthly(a, a.production.byMonth, (r) => r.netPremium.amountMinor);
    const pol = monthly(a, a.production.byMonth, (r) => r.policies);
    html += card(
      'Producción por mes',
      `<p class="muted small card-pad">Prima neta emitida en ${CURRENCY_NAME[cur]}, por mes de inicio de vigencia.</p>
      ${columnChart({ label: `Prima neta por mes en ${CURRENCY_NAME[cur]}`, categories, series: [{ name: 'Prima neta', color: '--viz-1', values: net }], value, axis })}
      ${tableFallback(
        ['Mes', 'Pólizas', 'Prima neta'],
        a.window.months.map((p, i) => [period(p), String(pol[i] ?? 0), value(net[i] ?? 0)]),
      )}`,
      switcher,
    );
  }
  if (a.collections) {
    const billed = monthly(a, a.collections.byMonth, (r) => r.billed.amountMinor);
    const collected = monthly(a, a.collections.byMonth, (r) => r.collected.amountMinor);
    html += card(
      'Cobranza por mes',
      `<p class="muted small card-pad">Lo facturado (por fecha de vencimiento) frente a lo cobrado (por fecha de pago), en ${CURRENCY_NAME[cur]}.</p>
      ${columnChart({
        label: `Facturado y cobrado por mes en ${CURRENCY_NAME[cur]}`,
        categories,
        series: [
          { name: 'Facturado', color: '--viz-1', values: billed },
          { name: 'Cobrado', color: '--viz-2', values: collected },
        ],
        value,
        axis,
      })}
      ${tableFallback(
        ['Mes', 'Facturado', 'Cobrado'],
        a.window.months.map((p, i) => [period(p), value(billed[i] ?? 0), value(collected[i] ?? 0)]),
      )}`,
      a.production ? '' : switcher,
    );
    const aging = a.collections.aging.length
      ? a.collections.aging
          .map(
            (
              x,
            ) => `<div class="aging-row"><h3>En ${CURRENCY_NAME[x.currency]} <span class="muted">· ${esc(fmt(x.total))} por cobrar</span></h3>
          ${stackedBar({
            label: `Antigüedad de saldos en ${CURRENCY_NAME[x.currency]}`,
            parts: x.buckets.map((b) => ({
              name: AGING[b.bucket]?.[0] ?? b.bucket,
              color: AGING[b.bucket]?.[1] ?? '--aging-1',
              amount: b.amount.amountMinor,
              value: fmt(b.amount),
              detail: `${b.installments} ${b.installments === 1 ? 'recibo' : 'recibos'}`,
            })),
          })}
          ${tableFallback(
            ['Tramo', 'Recibos', 'Monto'],
            x.buckets.map((b) => [
              AGING[b.bucket]?.[0] ?? b.bucket,
              String(b.installments),
              fmt(b.amount),
            ]),
          )}</div>`,
          )
          .join('')
      : '<p class="muted card-pad">No hay recibos por cobrar.</p>';
    const unconfirmedNote = (a.collections.unconfirmed ?? []).length
      ? `<div class="card-pad"><h3 class="split-title">Sin confirmar con la aseguradora</h3>
        <p class="muted small">Recibos que cobra la aseguradora y que Nubi no leyó ayer ni hoy: el cliente pudo haberlos pagado ya. No cuentan como mora ni en la tasa de cobro.</p>
        ${table(
          ['<Aseguradora', 'Recibos', '<Leídos por última vez', 'Pendiente'],
          (a.collections.unconfirmed ?? []).map(
            (u) =>
              `<tr><td>${carrierCell(u.carrier)}</td><td class="num">${u.installments}</td><td>${u.lastSyncedOn ? esc(date(u.lastSyncedOn)) : 'Nunca'}</td><td class="num"><strong>${esc(fmt(u.pending))}</strong></td></tr>`,
          ),
          'Recibos sin confirmar con la aseguradora',
        )}</div>`
      : '';
    html += card(
      'Antigüedad de saldos',
      `<p class="muted small card-pad">Lo que falta cobrar hoy, por días de atraso desde el vencimiento.</p><div class="aging">${aging}</div>${unconfirmedNote}`,
    );
  }
  return html;
}

function statsTables(a: Analytics) {
  let html = '';
  const prodRow = (name: string, r: ProdRow) =>
    `<tr><td>${name}</td><td class="num">${r.policies}</td><td class="num">${esc(fmt(r.netPremium))}</td><td class="num">${esc(fmt(r.commission))}</td></tr>`;
  const prodHead = (first: string) => [`<${first}`, 'Pólizas', 'Prima neta', 'Comisión'];
  if (a.production) {
    const p = a.production;
    const blocks = [
      a.scope.production === 'AGENCY'
        ? [
            'Por agente',
            table(
              prodHead('Agente'),
              p.byAgent.map((r) => prodRow(esc(agentName(r.agentId)), r)),
              'Producción por agente',
            ),
          ]
        : null,
      [
        'Por aseguradora',
        table(
          prodHead('Aseguradora'),
          p.byCarrier.map((r) => prodRow(carrierCell(r.carrier), r)),
          'Producción por aseguradora',
        ),
      ],
      [
        'Por ramo',
        table(
          prodHead('Ramo'),
          p.byProductLine.map((r) => prodRow(esc(lineName(r.productLine)), r)),
          'Producción por ramo',
        ),
      ],
    ].filter((x): x is string[] => x !== null);
    html += card(
      'Producción',
      `<div class="stats-split">${blocks.map(([t, b]) => `<div><h3 class="split-title">${esc(t ?? '')}</h3>${b}</div>`).join('')}</div>`,
    );
  }
  if (a.collections) {
    html += card(
      'Pólizas con deuda más antigua',
      table(
        [
          '<Póliza',
          '<Aseguradora',
          ...(a.scope.collections === 'AGENCY' ? ['<Agente'] : []),
          'Recibos',
          'Vencido desde',
          'Días',
          'Saldo vencido',
        ],
        a.collections.topOverdue.map(
          (
            o,
          ) => `<tr><td><span class="mono nowrap">${esc(o.policyNumber)}</span><div class="muted small">${esc(lineName(o.productLine))}</div></td>
          <td>${carrierCell(o.carrier)}</td>${a.scope.collections === 'AGENCY' ? `<td>${esc(agentName(o.agentId))}</td>` : ''}
          <td class="num">${o.installments}</td><td class="num">${esc(date(o.oldestDueDate))}</td>
          <td class="num">${o.daysPastDue > 90 ? pill('DELINQUENT', `${o.daysPastDue}`) : o.daysPastDue}</td><td class="num"><strong>${esc(fmt(o.overdue))}</strong></td></tr>`,
        ),
        'Pólizas con deuda vencida',
      ),
      reportButtons('delinquency', 'la morosidad'),
    );
  }
  if (a.carrierMoney) {
    const m = a.carrierMoney;
    html += card(
      'Dinero de las aseguradoras',
      `<div class="stats-split"><div><h3 class="split-title">Cobrado por remesar</h3>${table(
        ['<Aseguradora', 'Recibos', 'Más antiguo', 'Por remesar'],
        m.pendingRemittance.map(
          (r) => `<tr><td>${carrierCell(r.carrier)}</td><td class="num">${r.installments}</td>
          <td class="num">${r.daysWaiting > 30 ? pill('OVERDUE', `hace ${r.daysWaiting} días`) : `hace ${r.daysWaiting} días`}</td><td class="num"><strong>${esc(fmt(r.pending))}</strong></td></tr>`,
        ),
        'Prima cobrada pendiente de remesar',
      )}</div>
      <div><h3 class="split-title">Estados de cuenta conciliados</h3>${table(
        ['<Estado de cuenta', 'Coincide', 'En disputa', 'Diferencia', 'Reclamo'],
        m.statements.map(
          (
            s,
          ) => `<tr><td>${carrierCell(s.carrier)} <span class="muted small">${esc(period(s.period))}</span></td>
          <td class="num">${esc(fmt(s.matched))}<div class="muted small">${s.matchedLines} líneas</div></td>
          <td class="num">${s.disputedLines ? `${esc(fmt(s.disputed))}<div class="muted small">${s.disputedLines} líneas</div>` : pill('MATCHED', 'Nada')}</td>
          <td class="num">${esc(fmt(s.difference))}</td><td class="num">${s.claim.amountMinor ? `<strong>${esc(fmt(s.claim))}</strong>` : esc(fmt(s.claim))}</td></tr>`,
        ),
        'Conciliación de los estados de cuenta',
      )}</div></div>`,
    );
  }
  if (a.commissions) {
    const c = a.commissions;
    const cells = (r: CommRow) =>
      [r.accrued, r.confirmed, r.disputed, r.settled, r.clawbacks]
        .map((x) => `<td class="num">${esc(fmt(x))}</td>`)
        .join('') + `<td class="num"><strong>${esc(fmt(r.total))}</strong></td>`;
    const head = ['Devengada', 'Confirmada', 'En disputa', 'Liquidada', 'Devoluciones', 'Total'];
    html += card(
      'Comisiones por agente y mes',
      `<p class="muted small card-pad">Devengada: la aseguradora recibió la prima. Confirmada: el estado de cuenta la reconoce. Liquidada: ya está en la liquidación de la agente. Las devoluciones por cancelación ya están restadas.</p>
      ${table(
        ['<Agente', '<Mes', ...head],
        [
          ...c.byAgentMonth.map(
            (r) =>
              `<tr><td>${esc(agentName(r.agentId))}${r.currency === 'USD' ? ' <span class="muted small">dólares</span>' : ''}</td><td>${esc(period(r.period))}</td>${cells(r)}</tr>`,
          ),
          ...c.totals.map(
            (r) =>
              `<tr class="total-row"><td><strong>Total${c.totals.length > 1 ? ` en ${CURRENCY_NAME[r.currency]}` : ''}</strong></td><td></td>${cells(r)}</tr>`,
          ),
        ],
        'Comisiones por agente y mes',
      )}`,
      reportButtons('commissions', 'las comisiones'),
    );
  }
  if (a.quoting) {
    const qs = a.quoting;
    html += card(
      'Cotización',
      `<div class="stats-split"><div><h3 class="split-title">Por ramo</h3>${table(
        ['<Ramo', 'Cotizaciones', 'Emitidas', 'Conversión'],
        qs.byProductLine.map(
          (r) =>
            `<tr><td>${esc(lineName(r.productLine))}</td><td class="num">${r.quotes}</td><td class="num">${r.issued}</td><td class="num">${rateText(r.rate)}</td></tr>`,
        ),
        'Cotizaciones por ramo',
      )}</div>
      <div><h3 class="split-title">Conversión por aseguradora</h3>${table(
        ['<Aseguradora', 'Cotizadas', 'Emitidas', 'Conversión'],
        qs.byCarrier.map(
          (r) =>
            `<tr><td>${carrierCell(r.carrier)}</td><td class="num">${r.quoted}</td><td class="num">${r.issued}</td><td class="num">${rateText(r.rate)}${meter(r.rate ?? 0, 'inline')}</td></tr>`,
        ),
        'Conversión de cotización a póliza por aseguradora',
      )}</div>
      <div><h3 class="split-title">Prima promedio cotizada</h3>${table(
        ['<Ramo', 'Cotizaciones', 'Promedio'],
        qs.averagePremium.map(
          (r) =>
            `<tr><td>${esc(lineName(r.productLine))}</td><td class="num">${r.quotes}</td><td class="num">${esc(fmt(r.average))}</td></tr>`,
        ),
        'Prima promedio de la opción más baja',
      )}</div>
      <div data-component="quotes-lost"><h3 class="split-title">Perdidas por motivo</h3>${table(
        ['<Motivo', 'Cotizaciones'],
        (qs.lostByReason ?? []).map(
          (r) =>
            `<tr><td>${esc(QUOTE_LOST_REASON[r.reason] ?? r.reason)}</td><td class="num">${r.quotes}</td></tr>`,
        ),
        'Cotizaciones cerradas como perdidas, por motivo',
      )}</div></div>`,
      reportButtons('quotes', 'las cotizaciones'),
    );
  }
  if (a.leads) html += leadStatsHtml(a.leads, a.scope.leads === 'AGENCY');
  if (a.operations) {
    const o = a.operations;
    html += card(
      'Órdenes de trabajo',
      `<div class="stats-split"><div><h3 class="split-title">Abiertas hoy</h3>${table(
        ['<Aseguradora', '<Etapa', 'Órdenes', 'Atrasadas'],
        o.open.map(
          (r) =>
            `<tr><td>${carrierCell(r.carrier)}</td><td>${pill(r.stage, STATUS[r.stage] ?? r.stage)}</td><td class="num">${r.orders}</td><td class="num">${r.overdue ? `<strong style="color:var(--warn)">${r.overdue}</strong>` : 0}</td></tr>`,
        ),
        'Órdenes abiertas por aseguradora y etapa',
      )}</div>
      <div><h3 class="split-title">Completadas en el rango</h3>${table(
        ['<Aseguradora', 'Órdenes', 'Horas promedio', 'Fuera de tiempo'],
        [
          ...o.closed.map(
            (r) =>
              `<tr><td>${carrierCell(r.carrier)}</td><td class="num">${r.orders}</td><td class="num">${r.averageHours ?? '—'}</td><td class="num">${r.late}</td></tr>`,
          ),
          ...(o.closed.length > 1
            ? [
                `<tr class="total-row"><td><strong>Total</strong></td><td class="num">${o.closedTotals.orders}</td><td class="num">${o.closedTotals.averageHours ?? '—'}</td><td class="num">${o.closedTotals.late}</td></tr>`,
              ]
            : []),
        ],
        'Órdenes completadas y su tiempo promedio',
      )}</div></div>`,
    );
  }
  if (a.renewals && a.renewals.total.due + a.renewals.total.pending > 0) {
    const r = a.renewals;
    const rate = (x: RenewalStats) => (x.rate === null ? '—' : `${Math.round(x.rate * 100)} %`);
    const money = (x: RenewalStats, k: 'retained' | 'lost') =>
      x.premium.map((p) => fmt(p[k])).join(' · ') || '—';
    const rows = (
      groups: { key: string; indicators: RenewalStats }[],
      name: (k: string) => string,
    ) =>
      groups.map(
        (g) =>
          `<tr><td>${name(g.key)}</td><td class="num">${g.indicators.due}</td><td class="num">${g.indicators.renewed}</td><td class="num">${rate(g.indicators)}</td><td class="num">${esc(money(g.indicators, 'retained'))}</td><td class="num">${esc(money(g.indicators, 'lost'))}</td></tr>`,
      );
    const head = (first: string) => [
      `<${first}`,
      'Decididas',
      'Renovadas',
      'Tasa',
      'Prima retenida',
      'Prima perdida',
    ];
    const reasons = Object.entries(r.total.lostByReason)
      .sort((x, y) => y[1] - x[1])
      .map(([k, n]) => `<span class="chip">${esc(RENEWAL_REASON[k] ?? k)} · ${n}</span>`)
      .join(' ');
    html += card(
      'Renovaciones',
      `<p class="muted small">Tasa ${rate(r.total)}: ${r.total.renewed} renovadas de ${r.total.due} pólizas cuya vigencia terminó en el rango (${r.total.pending} siguen en gestión).${reasons ? ` No renovadas por motivo: ${reasons}` : ''}</p>
      <div class="stats-split"><div>${table(
        head('Agente'),
        rows(r.byAgent, (k) => esc(agentName(k))),
        'Tasa de renovación por agente',
      )}</div>
      <div>${table(
        head('Aseguradora'),
        rows(r.byCarrier, (k) => carrierCell(k)),
        'Tasa de renovación por aseguradora',
      )}
      ${table(
        head('Ramo'),
        rows(r.byProductLine, (k) => esc(lineName(k))),
        'Tasa de renovación por ramo',
      )}</div></div>`,
      reportButtons('renewals', 'las renovaciones'),
    );
  }
  html += splitCard(a);
  html += cancellationsCard(a);
  html += portfolioCard(a);
  return html;
}

/** One cell per currency's amount, or a dash (quetzales and dollars never added). */
const amounts = (xs: Money[]) => esc(xs.map((m) => fmt(m)).join(' · ') || '—');
/** A currency's name after a key, when there are dollars: «Ana · dólares». */
const inCurrency = (c: Cur) => (c === 'USD' ? ' <span class="muted small">dólares</span>' : '');

/**
 * The agency's commission split (#318, ADR-0044): what accrued, what was reversed, the agents'
 * share and the agency's net, per month, carrier and line. Management only (the API sends null
 * to an agent).
 */
function splitCard(a: Analytics) {
  const s = a.commissions?.split;
  if (!s?.totals.length) return '';
  const head = (first: string) => [
    `<${first}`,
    'Devengada',
    'Revertida',
    'Cedida al agente',
    'Neta de la agencia',
  ];
  const row = (name: string, r: SplitRow) =>
    `<tr><td>${name}${inCurrency(r.currency)}</td>${[r.accrued, r.reversed, r.ceded]
      .map((x) => `<td class="num">${esc(fmt(x))}</td>`)
      .join('')}<td class="num"><strong>${esc(fmt(r.net))}</strong></td></tr>`;
  const totals = s.totals.map(
    (r) =>
      `<tr class="total-row"><td><strong>Total${s.totals.length > 1 ? ` en ${CURRENCY_NAME[r.currency]}` : ''}</strong></td>${[
        r.accrued,
        r.reversed,
        r.ceded,
      ]
        .map((x) => `<td class="num">${esc(fmt(x))}</td>`)
        .join('')}<td class="num"><strong>${esc(fmt(r.net))}</strong></td></tr>`,
  );
  const unshared = s.totals.reduce((x, r) => x + r.unshared, 0);
  return card(
    'Comisión neta de la agencia',
    `<p class="muted small card-pad">Devengada: comisiones y ajustes del mes. Revertida: lo que las aseguradoras tomaron de vuelta. Cedida: la parte de cada agente, con la tasa de su ramo. Neta: lo que le queda a la agencia.${unshared ? ` ${unshared === 1 ? '1 línea no tiene' : `${unshared} líneas no tienen`} tasa del agente para su ramo: queda completa en la neta.` : ''}</p>
    <div class="stats-stack" data-component="commission-split"><div><h3 class="split-title">Por mes</h3>${table(
      head('Mes'),
      [...s.byMonth.map((r) => row(esc(period(r.period)), r)), ...totals],
      'Comisión devengada, cedida y neta por mes',
    )}</div>
    <div><h3 class="split-title">Por aseguradora</h3>${table(
      head('Aseguradora'),
      s.byCarrier.map((r) => row(carrierCell(r.carrier), r)),
      'Comisión devengada, cedida y neta por aseguradora',
    )}</div>
    <div><h3 class="split-title">Por ramo</h3>${table(
      head('Ramo'),
      s.byProductLine.map((r) => row(esc(lineName(r.productLine)), r)),
      'Comisión devengada, cedida y neta por ramo',
    )}</div></div>`,
  );
}

/** Why a policy was cancelled, as the console says it (#311). */
const CANCELLATION_REASON: Record<string, string> = {
  CUSTOMER_REQUEST: 'A solicitud del cliente',
  RISK_TRANSFERRED: 'Riesgo transferido',
  NON_PAYMENT: 'Falta de pago',
  UNKNOWN: 'Sin motivo registrado',
};

/** Cancellations with effect in the range (#318): how many, the premium lost and why. */
function cancellationsCard(a: Analytics) {
  const c = a.cancellations;
  if (!c) return '';
  const n = c.totals.reduce((x, t) => x + t.policies, 0);
  const head = (first: string) => [`<${first}`, 'Pólizas', 'Prima perdida'];
  const row = (name: string, r: Count) =>
    `<tr><td>${name}${inCurrency(r.currency)}</td><td class="num">${r.policies}</td><td class="num"><strong>${esc(fmt(r.premium))}</strong></td></tr>`;
  const blocks: [string, string][] = [
    [
      'Por motivo',
      table(
        head('Motivo'),
        c.byReason.map((r) => row(esc(CANCELLATION_REASON[r.reason] ?? r.reason), r)),
        'Anulaciones por motivo',
      ),
    ],
    [
      'Por mes',
      table(
        head('Mes'),
        c.byMonth.map((r) => row(esc(period(r.period)), r)),
        'Anulaciones por mes de su fecha efectiva',
      ),
    ],
    [
      'Por aseguradora',
      table(
        head('Aseguradora'),
        c.byCarrier.map((r) => row(carrierCell(r.carrier), r)),
        'Anulaciones por aseguradora',
      ),
    ],
    [
      'Por ramo',
      table(
        head('Ramo'),
        c.byProductLine.map((r) => row(esc(lineName(r.productLine)), r)),
        'Anulaciones por ramo',
      ),
    ],
  ];
  if (a.scope.cancellations === 'AGENCY')
    blocks.push([
      'Por agente',
      table(
        head('Agente'),
        c.byAgent.map((r) => row(esc(agentName(r.agentId)), r)),
        'Anulaciones por agente',
      ),
    ]);
  return card(
    'Anulaciones',
    `<p class="muted small card-pad">${n === 1 ? '1 póliza anulada' : `${n} pólizas anuladas`} con fecha efectiva en el rango${n ? `: ${amounts(c.totals.map((t) => t.premium))} de prima neta anual perdida` : ''}.${c.undated ? ` ${c.undated === 1 ? 'Otra anulada no tiene' : `Otras ${c.undated} anuladas no tienen`} fecha registrada (de antes de Nubi) y no se cuenta en ningún mes.` : ''}</p>
    <div class="stats-split" data-component="cancellations">${blocks.map(([t, b]) => `<div><h3 class="split-title">${esc(t)}</h3>${b}</div>`).join('')}</div>`,
    reportButtons('cancellations', 'las anulaciones'),
  );
}

/** «2 años y 3 meses» from a number of days (a year of 365 days, a month of 30). */
export function tenureText(days: number | null) {
  if (days === null) return '—';
  const years = Math.floor(days / 365);
  const months = Math.floor((days % 365) / 30);
  const y = years === 1 ? '1 año' : `${years} años`;
  const m = months === 1 ? '1 mes' : `${months} meses`;
  if (!years) return months ? m : 'menos de un mes';
  return months ? `${y} y ${m}` : y;
}

/**
 * New business against renewals and the portfolio per customer (#318): customers with one
 * policy or several, their mean tenure and the premium per customer, on the range's last day.
 */
function portfolioCard(a: Analytics) {
  const p = a.portfolio;
  if (!p) return '';
  const nb = p.newBusiness;
  const kind = (k: string) => (k === 'NEW' ? 'Nueva' : 'Renovación');
  const share = p.customers ? p.multiPolicy / p.customers : 0;
  const premium = table(
    ['<Moneda', 'Clientes', 'Prima neta anual', 'Prima por cliente'],
    p.premium.map(
      (x) =>
        `<tr><td>${esc(CURRENCY_NAME[x.currency])}</td><td class="num">${x.customers}</td><td class="num">${esc(fmt(x.premium))}</td><td class="num"><strong>${esc(fmt(x.perCustomer))}</strong></td></tr>`,
    ),
    'Prima por cliente',
  );
  const production = table(
    ['<Tipo', 'Pólizas', 'Prima neta anual'],
    nb.totals.map(
      (r) =>
        `<tr><td>${kind(r.kind)}${inCurrency(r.currency)}</td><td class="num">${r.policies}</td><td class="num"><strong>${esc(fmt(r.premium))}</strong></td></tr>`,
    ),
    'Producción nueva y renovaciones',
  );
  const byMonth = table(
    ['<Mes', '<Tipo', 'Pólizas', 'Prima neta anual'],
    nb.byMonth.map(
      (r) =>
        `<tr><td>${esc(period(r.period))}</td><td>${kind(r.kind)}${inCurrency(r.currency)}</td><td class="num">${r.policies}</td><td class="num">${esc(fmt(r.premium))}</td></tr>`,
    ),
    'Producción nueva y renovaciones por mes',
  );
  return card(
    'Cartera y producción nueva',
    `<p class="muted small card-pad">Al ${esc(date(p.on))}: ${p.customers === 1 ? '1 cliente' : `${p.customers} clientes`} con ${p.policies === 1 ? '1 póliza vigente' : `${p.policies} pólizas vigentes`}; ${p.singlePolicy} con una sola póliza y ${p.multiPolicy} con varias. Antigüedad media: ${esc(tenureText(p.averageTenureDays))}.${p.withoutCustomer ? ` ${p.withoutCustomer === 1 ? '1 póliza vigente no tiene' : `${p.withoutCustomer} pólizas vigentes no tienen`} cliente registrado y no se cuenta${p.withoutCustomer === 1 ? '' : 'n'}.` : ''}</p>
    <div class="card-pad">${meter(share)}<p class="muted small">${Math.round(share * 100)} % de los clientes tiene más de una póliza.</p></div>
    <div class="stats-stack" data-component="portfolio"><div class="stats-split"><div><h3 class="split-title">Prima por cliente</h3>${premium}</div>
    <div><h3 class="split-title">Nueva frente a renovación</h3>${production}</div></div>
    <div><h3 class="split-title">Producción por mes de inicio</h3>${byMonth}</div></div>
    <div class="card-pad report-list">${[
      ['portfolio', 'Cartera de pólizas', 'la cartera de pólizas'],
      ['customers', 'Clientes', 'los clientes'],
      ['production', 'Producción', 'la producción'],
    ]
      .map(
        ([r, label, what]) =>
          `<div class="report-row"><span>${esc(label)}</span>${reportButtons(r as string, what as string)}</div>`,
      )
      .join('')}</div>`,
  );
}

/** Why a renewal was lost, as «Renovaciones» says it. */
const RENEWAL_REASON: Record<string, string> = {
  PRICE: 'Precio',
  OTHER_CARRIER: 'Se fue a otra aseguradora',
  SOLD_ASSET: 'Vendió el bien',
  NON_PAYMENT: 'Falta de pago',
  OTHER: 'Otro motivo',
  NO_FOLLOW_UP: 'Sin gestión',
};

/** Nothing at all in the range: say so instead of a page of zeros. */
function statsEmpty(a: Analytics) {
  return (
    !a.production?.totals.length &&
    !a.collections?.byMonth.length &&
    !a.collections?.aging.length &&
    !a.carrierMoney?.pendingRemittance.length &&
    !a.carrierMoney?.statements.length &&
    !a.commissions?.totals.length &&
    !a.quoting?.totals.quotes &&
    !a.leads?.total.leads &&
    !a.operations?.openTotals.orders &&
    !a.operations?.closedTotals.orders &&
    !a.renewals?.total.due &&
    !a.renewals?.total.pending &&
    !a.cancellations?.totals.length &&
    !a.cancellations?.undated &&
    !a.portfolio?.customers &&
    !a.portfolio?.policies &&
    !a.portfolio?.newBusiness.totals.length &&
    !a.claims?.reported
  );
}

export async function estadisticas() {
  if (!me) return '';
  const a = await api<Analytics>('GET', `/v1/analytics${statsQuery()}`);
  const own = a.agents.length <= 1 && !!me.agentId;
  let html = pageHead(
    own ? 'Mis estadísticas' : 'Estadísticas',
    own
      ? 'Tu producción, tu cobranza y tus comisiones, mes a mes.'
      : 'Producción, cobranza, dinero de las aseguradoras, comisiones y operación de la agencia, mes a mes.',
  );
  html += statsFilters(a);
  if (statsEmpty(a))
    return (
      html +
      empty(
        'inbox',
        'Sin movimientos en este rango',
        'Amplía el rango de meses o quita un filtro para ver cifras.',
        '',
        2,
      )
    );
  html += statsKpis(a);
  html += statsCharts(a);
  html += statsTables(a);
  html += claimsStats(a.claims);
  return html;
}

/**
 * Claims in the range (#309, ADR-0038): per status, carrier and line, the mean days to resolve
 * and the loss ratio (paid ÷ earned premium), with the statistics' filters (#318: a section of
 * /v1/analytics; an agent: their own). Nothing when the person may not see them or there are none.
 */
export function claimsStats(ind: ClaimIndicators | null | undefined) {
  if (!ind || (!ind.reported && !ind.lossRatioByCarrier.length)) return '';
  const statuses = Object.entries(ind.byStatus)
    .filter(([, n]) => n > 0)
    .map(([k, n]) => `<span class="chip">${esc(CLAIM_STATUS[k]?.[0] ?? k)} · ${n}</span>`)
    .join(' ');
  const rows = (groups: ClaimIndicators['byCarrier'], name: (k: string) => string) =>
    groups.map(
      (g) =>
        `<tr><td>${name(g.key)}</td><td class="num">${g.reported}</td><td class="num">${g.open}</td></tr>`,
    );
  return (
    card(
      'Siniestros',
      `<p class="muted small card-pad">${ind.reported === 1 ? '1 siniestro avisado' : `${ind.reported} siniestros avisados`} en el rango${statuses ? `: ${statuses}` : '.'} Tiempo medio de resolución: ${ind.meanResolutionDays === null ? '—' : `${ind.meanResolutionDays} días`}.</p>
      <div class="stats-split"><div>${table(
        ['<Aseguradora', 'Avisados', 'Abiertos'],
        rows(ind.byCarrier, (k) => carrierCell(k)),
        'Siniestros por aseguradora',
      )}</div>
      <div>${table(
        ['<Ramo', 'Avisados', 'Abiertos'],
        rows(ind.byProductLine, (k) => esc(lineName(k))),
        'Siniestros por ramo',
      )}</div></div>`,
      reportButtons('claims', 'los siniestros'),
    ) + lossRatios(ind)
  );
}

/** Updates one filter and repaints; a range that ends before it starts is read the other way. */
const applyStatsFilter = (sel: HTMLSelectElement) => {
  const key = {
    'st-from': 'from',
    'st-to': 'to',
    'st-carrier': 'carrier',
    'st-agent': 'agent',
    'st-line': 'productLine',
  }[sel.id] as keyof typeof statsFilter | undefined;
  if (key) statsFilter[key] = sel.value;
  if (statsFilter.from && statsFilter.to && statsFilter.from > statsFilter.to)
    [statsFilter.from, statsFilter.to] = [statsFilter.to, statsFilter.from];
  void render('estadisticas', { quiet: true }).then(() => $(`#${sel.id}`)?.focus());
};

/** What this view answers: its buttons (by data attribute), forms (by id) and controls (by id). */
export const actions: Actions = {
  click: {
    statsCurrency: (_, d) => {
      statsCurrency = d.statsCurrency === 'USD' ? 'USD' : 'GTQ';
      void render('estadisticas', { quiet: true }).then(() =>
        $(`[data-stats-currency="${statsCurrency}"]`)?.focus(),
      );
    },
  },
  submit: {
    'stats-filters': (ev) => ev.preventDefault(),
  },
  change: {
    'st-from': applyStatsFilter,
    'st-to': applyStatsFilter,
    'st-carrier': applyStatsFilter,
    'st-agent': applyStatsFilter,
    'st-line': applyStatsFilter,
  },
};
